import { ServiceSettingServiceGroupStore } from './service-setting-service-group.store';
import { routeParams, translate } from '../../../../modules/shared/shared.global-service';
import { Component, computed, inject, input, viewChild } from '@angular/core';
import {
    activeColumn,
    colorColumn,
    descriptionColumn,
    nameColumn,
    validFromColumn,
    validToColumn,
} from '../../../../modules/shared/components/kit/table/table.fields';
import { ServiceGroupObject } from '@libs/types/src/services/object-type/service/service-group.object';
import {
    ServiceGroupsUpsertDialogComponent,
} from '../../../../modules/content/components/services/settings/service-groups-upsert-dialog/service-groups-upsert-dialog.component';
import { contentGlobalSuccess } from '../../../../modules/content/content.error';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { FormGroup } from '@angular/forms';
import { ServiceGroupEnum, serviceGroupHasValidity } from '@libs/types/src/services/enum/service-group.enum';
import { serviceGroupItemFrag, serviceOfferGroupItemFrag } from './service-setting-service-group.query';
import { TableColumn } from '../../../../modules/shared/components/kit/table/table.types';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import { CardTableComponent } from '../../../../modules/shared/components/kit/table/card-table.component';
import {
    serviceGroupTypeEnumOptions,
} from '@libs/types/src/services/enum/service-group-type.enum';
import {
    SocialCreditGroupDialogComponent,
} from '../../../social-service/setting/social-credit-group-dialog/social-credit-group-dialog.component';
import { SocialCreditGroupSettings } from '@libs/types/src/services/types/social-credit-group-settings.types';

@Component({
    selector: 'app-service-setting-service-group',
    templateUrl: './service-setting-service-group.component.html',
    styleUrl: './service-setting-service-group.component.scss',
    providers: [ServiceSettingServiceGroupStore],
    imports: [
        CardTableComponent,
    ],
})
export class ServiceSettingServiceGroupComponent {

    protected readonly dialogService = inject(BaseDialogService);
    protected readonly confirm = inject(ConfirmBuilder);
    protected readonly store = inject(ServiceSettingServiceGroupStore);

    public settingsForm = input<FormGroup | null>(null);
    public groupType = input.required<string>();
    public groupTypeSuffix = computed(() => {
        const g = this.groupType();
        return g.substring(0, 1).toUpperCase() + g.substring(1);
    });

    protected recordsTable = viewChild<CardTableComponent>('serviceGroups');

    protected additionalFilter = computed(() => {
        return [
            [
                {
                    column: 'serviceId',
                    operator: 'EQ',
                    value: [routeParams.serviceId()],
                },
            ],
            [
                {
                    column: 'type',
                    operator: 'EQ',
                    value: [this.groupType()],
                },
            ],
        ];
    });

    protected columns = computed(() => {
        if (this.groupType() === ServiceGroupEnum.CREDITS) return this.creditColumns;

        const hasValidity = serviceGroupHasValidity(this.groupType());
        const offers = this.groupType() === ServiceGroupEnum.OFFERS;

        return [
            activeColumn,
            colorColumn,
            hasValidity ? validFromColumn : null,
            hasValidity ? validToColumn : null,
            {
                name: 'groupType',
                data: (item: string) => translate.instant(`service.settings.group.groupTypes.${item}`),
                filterable: {
                    type: 'select',
                    selectOptions: () => serviceGroupTypeEnumOptions,
                },
            },
            nameColumn,
            offers ? {
                name: 'offersCount',
                filterable: false,
            } : null,
            descriptionColumn,
        ].filter((it) => it) as TableColumn[];
    });

    private readonly creditColumns: TableColumn[] = [
        activeColumn,
        colorColumn,
        nameColumn,
        {
            name: 'creditAmount',
            dataKey: 'settings',
            data: (settings: string) => (JSON.parse(settings) as SocialCreditGroupSettings).creditAmount,
            filterable: { type: 'number' },
        },
        validFromColumn,
        validToColumn,
        {
            name: 'creditValidityDays',
            dataKey: 'settings',
            data: (settings: string) => (JSON.parse(settings) as SocialCreditGroupSettings).creditValidityDays,
            filterable: { type: 'number' },
        },
    ];

    protected openDialog(item: ServiceGroupObject | null = null) {
        const ref = this.dialogService.open(
            this.groupType() === ServiceGroupEnum.CREDITS
                ? SocialCreditGroupDialogComponent : ServiceGroupsUpsertDialogComponent,
            {
                header: translate.instant('service.settings.group.label' + this.groupTypeSuffix()),
                styleClass: 'p-dialog-width-50',
                data: {
                    serviceId: routeParams.serviceId(),
                    serviceGroup: item,
                    groupType: this.groupType(),
                    settingsForm: this.settingsForm(),
                },
            });

        ref?.onClose.subscribe((res) => {
            if (res && res.refresh) {
                this.recordsTable()?.reload();
            }
        });
    }

    protected async deleteGroup(event: any) {
        this.confirm.confirmDelete(
            async () => {
                this.store.api.deleteServiceGroup({ id: event.item.id }).then(() => {
                    contentGlobalSuccess.next('common.deleted');
                    this.recordsTable()?.reload();
                });
            },
            event,
        );
    }

    protected readonly routeParams = routeParams;

    protected readonly serviceGroupItemFrag = serviceGroupItemFrag;
    protected readonly ServiceGroupEnum = ServiceGroupEnum;
    protected readonly serviceOfferGroupItemFrag = serviceOfferGroupItemFrag;
}
